import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PackageSearch, ArrowUpRight } from "lucide-react";

// Order Soon dashboard card (replaces Inventory Intelligence in the same slot). Whole card opens /order-soon.
export function OrderSoonCard() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useQuery({ queryKey: ["order-soon"], queryFn: async () => (await api.get("/inventory/order-soon")).data });
  const c = data?.counts || {};
  const open = (tab) => navigate(`/order-soon${tab ? `?tab=${tab}` : ""}`);
  const onKey = (e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); open(); } };
  return (
    <div role="link" tabIndex={0} onClick={() => open()} onKeyDown={onKey} data-testid="order-soon-card"
      aria-label={`Order Soon: ${c.total ?? 0} products need replenishment`}
      className="group cursor-pointer rounded-3xl border border-slate-200 bg-white p-4 sm:p-5 transition-colors hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-pawkt-primary-deep">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-slate-700">
          <PackageSearch className="h-4 w-4 text-pawkt-primary-deep" /><span className="text-sm font-bold">Order Soon</span>
          {data && <span className="text-xs text-slate-400">· below {data.threshold_pct}% of stock target</span>}
        </div>
        <ArrowUpRight className="h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </div>
      {isLoading ? <div className="mt-3 h-14 animate-pulse rounded-xl bg-slate-100" /> : isError ? (
        <p className="mt-3 text-sm text-rose-600" data-testid="order-soon-error">Couldn't load replenishment data.</p>
      ) : c.total === 0 ? (
        <p className="mt-3 text-sm text-slate-500" data-testid="order-soon-empty">
          Nothing to order right now.{c.not_configured ? ` ${c.not_configured} product${c.not_configured > 1 ? "s have" : " has"} no stock target yet.` : ""}
        </p>
      ) : (
        <div className="mt-3 flex flex-wrap items-end gap-6">
          <div><div className="font-display text-3xl font-extrabold tabular text-slate-900" data-testid="order-soon-total">{c.total}</div>
            <div className="text-xs text-slate-500">products to reorder</div></div>
          <button type="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); open("customer"); }} className="text-left" data-testid="order-soon-customer">
            <div className="text-lg font-bold tabular text-slate-800">{c.customer}</div><div className="text-xs text-slate-500">Customer sales</div></button>
          <button type="button" tabIndex={-1} onClick={(e) => { e.stopPropagation(); open("internal"); }} className="text-left" data-testid="order-soon-internal">
            <div className="text-lg font-bold tabular text-slate-800">{c.internal}</div><div className="text-xs text-slate-500">Internal consumption</div></button>
          {c.not_configured > 0 && <div className="text-xs text-amber-700" data-testid="order-soon-unconfigured">{c.not_configured} without stock target</div>}
        </div>
      )}
    </div>
  );
}
